import { useState, useEffect, useCallback } from "react";
import { MessageCircle, Send, ArrowLeft, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function MessagesButton() {
  const [open, setOpen] = useState(false);
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [unreadTotal, setUnreadTotal] = useState(0);

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/messages"); setConvos(data.conversations); setUnreadTotal(data.conversations.reduce((a, c) => a + c.unread, 0)); } catch {}
  }, []);

  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 20000); return () => clearInterval(t); }, [loadConvos]);

  const openThread = async (c) => {
    setActive(c); setThread(null);
    try { const { data } = await api.get(`/messages/${c.match_id}`); setThread(data); loadConvos(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post(`/messages/${active.match_id}`, { text });
      setText("");
      const { data } = await api.get(`/messages/${active.match_id}`); setThread(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <button className="relative p-2 text-zinc-300 hover:text-white" onClick={() => { setOpen(true); setActive(null); loadConvos(); }} data-testid="messages-button" aria-label="Messages">
        <MessageCircle className="w-5 h-5" />
        {unreadTotal > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E53E3E] text-white text-[10px] font-bold grid place-items-center">{unreadTotal}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0B] border-zinc-800 max-w-lg h-[70vh] flex flex-col" data-testid="messages-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black uppercase tracking-tight flex items-center gap-2">
              {active && <button onClick={() => setActive(null)} data-testid="messages-back"><ArrowLeft className="w-5 h-5" /></button>}
              {active ? active.name : "Messages"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">Private messaging with your active accountability partner.</DialogDescription>
          </DialogHeader>

          {!active ? (
            <div className="flex-1 overflow-y-auto -mx-6">
              {convos.length === 0 ? <p className="px-6 text-sm text-zinc-500">No conversations yet. Messaging opens once you're actively matched.</p> :
                convos.map((c) => (
                  <button key={c.match_id} onClick={() => openThread(c)} className="w-full text-left px-6 py-3 hover:bg-zinc-900 border-b border-zinc-800/60 flex items-center justify-between" data-testid={`convo-${c.match_id}`}>
                    <div>
                      <p className="font-medium flex items-center gap-2">{c.name}{c.status === "ended" && <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">Ended</span>}</p>
                      <p className="text-xs text-zinc-500 truncate max-w-[280px]">{c.preview || "No messages yet"}</p>
                    </div>
                    {c.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#E53E3E] text-white text-[10px] font-bold grid place-items-center">{c.unread}</span>}
                  </button>
                ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-2 py-2" data-testid="messages-thread">
                {!thread ? <div className="grid place-items-center h-full"><Loader2 className="w-5 h-5 animate-spin text-[#E53E3E]" /></div> :
                  thread.messages.length === 0 ? <p className="text-sm text-zinc-500 text-center mt-6">Say hello 👋</p> :
                    thread.messages.map((m) => (
                      <div key={m.id} className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.mine ? "ml-auto bg-[#E53E3E] text-white" : "bg-zinc-800 text-zinc-100"}`}>{m.text}</div>
                    ))}
              </div>
              {thread && !thread.can_send ? (
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-center" data-testid="ended-conversation-notice">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Past Conversation — Ended</p>
                  <p className="text-xs text-zinc-500 mt-1">This relationship has ended. You can read the history, but new messages are disabled.</p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." className="h-11 bg-zinc-900 border-zinc-800" data-testid="message-input" />
                  <Button onClick={send} className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full h-11 px-4" data-testid="message-send"><Send className="w-4 h-4" /></Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
